/**
 * /admin — Private analytics dashboard
 *
 * Protected by ANALYTICS_TOKEN header (set VITE_ANALYTICS_TOKEN in .env).
 * Shows: summary KPIs, daily chart, funnel, top events, user emails, live feed, scroll depth.
 */

import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import {
  Zap, Users, RefreshCw, TrendingUp, MousePointer, AlertTriangle,
  Mail, Activity, BarChart3, Lock, LogIn, ArrowRight,
} from "lucide-react";

const viteEnv = (import.meta as ImportMeta & { env?: Record<string, string | undefined> }).env;
const ENV_TOKEN = viteEnv?.VITE_ANALYTICS_TOKEN ?? "";

type Summary = {
  period_days: number;
  total_sessions: number;
  unique_visitors: number;
  returning_visitors: number;
  conversions: number;
  conversions_today: number;
  signed_up: number;
  url_copies: number;
  errors: number;
};

type TopEvent = { event: string; count: number; unique_visitors: number };
type DailyConversion = { day: string; conversions: number };
type DailySession = { day: string; sessions: number; visitors: number };
type FunnelData = { visited: number; spec_uploaded: number; converted: number; url_copied: number; signed_in: number };
type UserRow = { email: string; first_seen_at: string; last_seen_at: string; converted: number; conversion_count: number; sign_up_at: string | null };
type RecentEvent = { event: string; page: string | null; properties: Record<string, unknown>; created_at: string; email: string | null; visitor_id: string | null };
type ScrollData = { total_sessions: number; buckets: Record<string, number> };

type InsightsData = {
  period_days: number;
  popular_apis: { api_title: string; base_url: string; conversions: number; unique_users: number }[];
  top_tools: { tool_name: string; spec_title: string; calls: number; avg_response_ms: number }[];
  top_api_errors: { api_title: string; server_id: string; failures: number; sample_error: string | null }[];
  upgrade_funnel: {
    signed_up: number;
    mcp_created: number;
    user_upgraded: number;
    upgrade_rate_pct: number;
  };
  performance: {
    successful_calls: number;
    failed_calls: number;
    error_rate_pct: number;
    avg_response_ms: number;
    min_response_ms: number;
    max_response_ms: number;
  };
};

// ── Fetch helpers ─────────────────────────────────────────────────────────────
async function apiFetch<T>(path: string, token: string): Promise<T> {
  const res = await fetch(path, {
    headers: { "x-analytics-token": token },
    credentials: "include",
  });
  if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
  return res.json() as Promise<T>;
}

// ── Stat card ─────────────────────────────────────────────────────────────────
function KpiCard({
  icon: Icon, label, value, sub, color,
}: { icon: React.ElementType; label: string; value: string | number; sub?: string; color: string }) {
  return (
    <div className="glass-strong rounded-2xl p-5 shadow-premium">
      <div className={`w-9 h-9 rounded-xl ${color} flex items-center justify-center mb-3`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="text-2xl font-bold text-foreground tabular-nums">{value}</div>
      <div className="text-xs font-semibold text-foreground mt-0.5">{label}</div>
      {sub && <div className="text-xs text-muted-foreground mt-0.5">{sub}</div>}
    </div>
  );
}

// ── Tiny bar chart ────────────────────────────────────────────────────────────
type ChartRow = { day: string; [key: string]: string | number };

function MiniBarChart({ data, valueKey, colorClass }: {
  data: ChartRow[];
  valueKey: string;
  colorClass: string;
}) {
  const max = Math.max(...data.map((d) => Number(d[valueKey]) ?? 0), 1);
  return (
    <div className="flex items-end gap-0.5 h-16 w-full">
      {data.map((d, i) => {
        const val = Number(d[valueKey]) ?? 0;
        const pct = (val / max) * 100;
        return (
          <div key={i} className="flex-1 flex flex-col items-center gap-0.5 group relative">
            <div
              className={`w-full rounded-sm ${colorClass} transition-all`}
              style={{ height: `${Math.max(pct, 2)}%` }}
            />
            <div className="absolute -top-6 left-1/2 -translate-x-1/2 bg-foreground text-background text-[10px] px-1.5 py-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10">
              {String(d["day"])?.slice(5)} · {val}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Funnel bar ────────────────────────────────────────────────────────────────
function FunnelStep({ label, value, max, color }: { label: string; value: number; max: number; color: string }) {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-xs">
        <span className="text-foreground font-medium">{label}</span>
        <span className="text-muted-foreground tabular-nums">{value.toLocaleString()} <span className="opacity-60">({pct}%)</span></span>
      </div>
      <div className="h-2 bg-muted rounded-full overflow-hidden">
        <div className={`h-full ${color} rounded-full transition-all`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
const AdminPage = () => {
  const [authed, setAuthed] = useState(false);
  const [tokenInput, setTokenInput] = useState(ENV_TOKEN);
  const [activeToken, setActiveToken] = useState(ENV_TOKEN);
  const [days, setDays] = useState(7);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [topEvents, setTopEvents] = useState<TopEvent[]>([]);
  const [dailyConversions, setDailyConversions] = useState<DailyConversion[]>([]);
  const [dailySessions, setDailySessions] = useState<DailySession[]>([]);
  const [funnel, setFunnel] = useState<FunnelData | null>(null);
  const [users, setUsers] = useState<UserRow[]>([]);
  const [recent, setRecent] = useState<RecentEvent[]>([]);
  const [scroll, setScroll] = useState<ScrollData | null>(null);
  const [insights, setInsights] = useState<InsightsData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);

  const load = useCallback(async (tokenOverride?: string) => {
    const token = tokenOverride ?? activeToken;
    setLoading(true);
    setError(null);
    try {
      const [sum, events, daily, fun, usr, rec, scr, ins] = await Promise.all([
        apiFetch<Summary>(`/api/analytics/report?days=${days}`, token),
        apiFetch<TopEvent[]>(`/api/analytics/report/events?days=${days}`, token),
        apiFetch<{ conversions: DailyConversion[]; sessions: DailySession[] }>(`/api/analytics/report/daily?days=${days}`, token),
        apiFetch<FunnelData>(`/api/analytics/report/funnel?days=${days}`, token),
        apiFetch<UserRow[]>(`/api/analytics/report/users?limit=50`, token),
        apiFetch<RecentEvent[]>(`/api/analytics/report/recent?limit=40`, token),
        apiFetch<ScrollData>(`/api/analytics/report/scroll?days=${days}`, token),
        apiFetch<InsightsData>(`/api/analytics/report/insights?days=${days}`, token),
      ]);
      setSummary(sum);
      setTopEvents(events);
      setDailyConversions(daily.conversions);
      setDailySessions(daily.sessions);
      setFunnel(fun);
      setUsers(usr);
      setRecent(rec);
      setScroll(scr);
      setInsights(ins);
      setLastRefresh(new Date());
      setAuthed(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load");
      if ((e as Error)?.message?.startsWith("403")) setAuthed(false);
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    if (authed && activeToken) void load();
  }, [days]); // reload when period changes

  const tryLogin = async () => {
    const t = tokenInput.trim();
    if (!t) return;
    setLoading(true);
    setError(null);
    try {
      await apiFetch<Summary>(`/api/analytics/report?days=7`, t);
      setActiveToken(t);
      setAuthed(true);
      void load(t);
    } catch (e) {
      const message = e instanceof Error ? e.message : "";
      if (message.startsWith("403")) {
        setError("Invalid token — check ANALYTICS_TOKEN on the Render backend");
      } else if (message.startsWith("404")) {
        setError("Analytics API not found — deploy the latest frontend so /api/analytics is proxied to Render");
      } else {
        setError(message || "Failed to reach the analytics API");
      }
    } finally {
      setLoading(false);
    }
  };

  // ── Login screen ─────────────────────────────────────────────────────────
  if (!authed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-sm glass-strong rounded-2xl p-8 shadow-premium"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
              <Lock className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="font-bold text-foreground">Admin Dashboard</div>
              <div className="text-xs text-muted-foreground">API → MCP Analytics</div>
            </div>
          </div>
          <input
            type="password"
            placeholder="Enter admin token"
            value={tokenInput}
            onChange={(e) => setTokenInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") void tryLogin(); }}
            className="w-full rounded-xl border border-border bg-background px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-ring/25 mb-3"
          />
          {error && <p className="text-red-500 text-xs mb-3">{error}</p>}
          <button
            type="button"
            onClick={() => void tryLogin()}
            disabled={loading || !tokenInput.trim()}
            className="w-full flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl btn-neu-primary text-sm font-semibold disabled:opacity-60"
          >
            {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <LogIn className="w-4 h-4" />}
            {loading ? "Checking…" : "Sign in"}
          </button>
        </motion.div>
      </div>
    );
  }

  // ── Dashboard ─────────────────────────────────────────────────────────────
  const fmtDate = (s: string) =>
    new Date(s).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });

  const returnRate = summary && summary.unique_visitors > 0
    ? Math.round((summary.returning_visitors / summary.unique_visitors) * 100)
    : 0;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="sticky top-0 z-40 border-b border-border/50 bg-background/80 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
              <Zap className="w-4 h-4 text-white" />
            </div>
            <span className="font-bold text-foreground text-sm">Analytics Dashboard</span>
            {lastRefresh && (
              <span className="text-xs text-muted-foreground hidden sm:block">
                Last updated {lastRefresh.toLocaleTimeString()}
              </span>
            )}
          </div>
          <div className="flex items-center gap-3">
            {/* Period selector */}
            <div className="flex items-center gap-1 border border-border rounded-xl overflow-hidden text-xs font-semibold">
              {[7, 14, 30].map((d) => (
                <button
                  key={d}
                  type="button"
                  onClick={() => setDays(d)}
                  className={`px-3 py-2 transition-colors ${
                    days === d ? "bg-primary text-white" : "text-muted-foreground hover:bg-muted"
                  }`}
                >
                  {d}d
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={() => void load()}
              disabled={loading}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border border-border text-xs font-semibold text-muted-foreground hover:text-foreground transition-colors disabled:opacity-60"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8 space-y-8">
        {error && (
          <div className="text-sm text-red-500 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900 rounded-xl px-4 py-3">
            {error}
          </div>
        )}

        {/* ── KPI grid ── */}
        {summary && (
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
            <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-4">
              Last {days} days
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
              <KpiCard
                icon={Zap} label="Conversions" value={summary.conversions}
                sub={`${summary.conversions_today} today`}
                color="bg-blue-50 text-blue-600 dark:bg-blue-950 dark:text-blue-400"
              />
              <KpiCard
                icon={Users} label="Unique Visitors" value={summary.unique_visitors}
                sub={`${summary.total_sessions} sessions`}
                color="bg-violet-50 text-violet-600 dark:bg-violet-950 dark:text-violet-400"
              />
              <KpiCard
                icon={TrendingUp} label="Return Rate" value={`${returnRate}%`}
                sub={`${summary.returning_visitors} returning`}
                color="bg-emerald-50 text-emerald-600 dark:bg-emerald-950 dark:text-emerald-400"
              />
              <KpiCard
                icon={Mail} label="Sign-ups" value={summary.signed_up}
                color="bg-amber-50 text-amber-600 dark:bg-amber-950 dark:text-amber-400"
              />
              <KpiCard
                icon={MousePointer} label="URL Copies" value={summary.url_copies}
                sub={`${summary.errors} errors`}
                color="bg-rose-50 text-rose-600 dark:bg-rose-950 dark:text-rose-400"
              />
            </div>
          </motion.div>
        )}

        {/* ── Charts row ── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Daily conversions chart */}
          <motion.div
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.05 }}
            className="glass-strong rounded-2xl p-6 shadow-premium"
          >
            <div className="flex items-center gap-2 mb-4">
              <Zap className="w-4 h-4 text-primary" />
              <h3 className="text-sm font-bold text-foreground">Daily Conversions</h3>
            </div>
            {dailyConversions.length > 0 ? (
              <MiniBarChart data={dailyConversions as ChartRow[]} valueKey="conversions" colorClass="bg-primary/80" />
            ) : (
              <div className="h-16 flex items-center justify-center text-xs text-muted-foreground">No data yet</div>
            )}
          </motion.div>

          {/* Daily sessions chart */}
          <motion.div
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.1 }}
            className="glass-strong rounded-2xl p-6 shadow-premium"
          >
            <div className="flex items-center gap-2 mb-4">
              <Users className="w-4 h-4 text-violet-500" />
              <h3 className="text-sm font-bold text-foreground">Daily Visitors</h3>
            </div>
            {dailySessions.length > 0 ? (
              <MiniBarChart data={dailySessions as ChartRow[]} valueKey="visitors" colorClass="bg-violet-400/70" />
            ) : (
              <div className="h-16 flex items-center justify-center text-xs text-muted-foreground">No data yet</div>
            )}
          </motion.div>
        </div>

        {/* ── Funnel + Scroll ── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Conversion funnel */}
          {funnel && (
            <motion.div
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.12 }}
              className="glass-strong rounded-2xl p-6 shadow-premium"
            >
              <div className="flex items-center gap-2 mb-5">
                <ArrowRight className="w-4 h-4 text-primary" />
                <h3 className="text-sm font-bold text-foreground">Conversion Funnel</h3>
              </div>
              <div className="space-y-4">
                <FunnelStep label="Visited site" value={funnel.visited} max={funnel.visited} color="bg-primary" />
                <FunnelStep label="Uploaded spec" value={funnel.spec_uploaded} max={funnel.visited} color="bg-violet-500" />
                <FunnelStep label="Generated MCP" value={funnel.converted} max={funnel.visited} color="bg-emerald-500" />
                <FunnelStep label="Copied MCP URL" value={funnel.url_copied} max={funnel.visited} color="bg-amber-500" />
                <FunnelStep label="Signed in/up" value={funnel.signed_in} max={funnel.visited} color="bg-rose-500" />
              </div>
            </motion.div>
          )}

          {/* Scroll depth */}
          {scroll && (
            <motion.div
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.14 }}
              className="glass-strong rounded-2xl p-6 shadow-premium"
            >
              <div className="flex items-center gap-2 mb-5">
                <BarChart3 className="w-4 h-4 text-primary" />
                <h3 className="text-sm font-bold text-foreground">Scroll Depth</h3>
                <span className="text-xs text-muted-foreground ml-auto">{scroll.total_sessions} sessions</span>
              </div>
              <div className="space-y-4">
                {Object.entries(scroll.buckets).map(([threshold, count]) => (
                  <FunnelStep
                    key={threshold}
                    label={`${threshold}% of page`}
                    value={count}
                    max={scroll.total_sessions || 1}
                    color="bg-blue-500"
                  />
                ))}
              </div>
            </motion.div>
          )}
        </div>

        {/* ── Product insights (MCP usage, errors, performance) ── */}
        {insights && (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.15 }}
            className="space-y-6"
          >
            <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
              Product insights
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <KpiCard
                icon={Zap}
                label="MCP calls (ok)"
                value={insights.performance.successful_calls}
                sub={`${insights.performance.error_rate_pct}% fail rate`}
                color="bg-emerald-50 text-emerald-600 dark:bg-emerald-950 dark:text-emerald-400"
              />
              <KpiCard
                icon={AlertTriangle}
                label="MCP failures"
                value={insights.performance.failed_calls}
                color="bg-red-50 text-red-600 dark:bg-red-950 dark:text-red-400"
              />
              <KpiCard
                icon={Activity}
                label="Avg latency"
                value={`${insights.performance.avg_response_ms}ms`}
                sub={`${insights.performance.min_response_ms}–${insights.performance.max_response_ms}ms`}
                color="bg-blue-50 text-blue-600 dark:bg-blue-950 dark:text-blue-400"
              />
              <KpiCard
                icon={TrendingUp}
                label="Upgrade rate"
                value={`${insights.upgrade_funnel.upgrade_rate_pct}%`}
                sub={`${insights.upgrade_funnel.user_upgraded} pro / ${insights.upgrade_funnel.mcp_created} MCPs`}
                color="bg-violet-50 text-violet-600 dark:bg-violet-950 dark:text-violet-400"
              />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="glass-strong rounded-2xl p-6 shadow-premium">
                <h3 className="text-sm font-bold text-foreground mb-4">Popular APIs (conversions)</h3>
                <div className="space-y-2 text-xs">
                  {insights.popular_apis.slice(0, 10).map((a, i) => (
                    <div key={`${a.api_title}-${i}`} className="flex justify-between gap-2">
                      <span className="truncate text-foreground font-medium">{a.api_title}</span>
                      <span className="tabular-nums text-muted-foreground shrink-0">{a.conversions}</span>
                    </div>
                  ))}
                  {insights.popular_apis.length === 0 && (
                    <p className="text-muted-foreground text-center py-4">No MCPs created yet</p>
                  )}
                </div>
              </div>
              <div className="glass-strong rounded-2xl p-6 shadow-premium">
                <h3 className="text-sm font-bold text-foreground mb-4">Top tools called</h3>
                <div className="space-y-2 text-xs">
                  {insights.top_tools.slice(0, 10).map((t, i) => (
                    <div key={`${t.tool_name}-${i}`} className="flex justify-between gap-2">
                      <span className="truncate font-mono text-foreground">{t.tool_name}</span>
                      <span className="tabular-nums text-muted-foreground shrink-0">
                        {t.calls} · {t.avg_response_ms}ms
                      </span>
                    </div>
                  ))}
                  {insights.top_tools.length === 0 && (
                    <p className="text-muted-foreground text-center py-4">No tool calls yet</p>
                  )}
                </div>
              </div>
              <div className="glass-strong rounded-2xl p-6 shadow-premium">
                <h3 className="text-sm font-bold text-foreground mb-4">APIs failing most</h3>
                <div className="space-y-2 text-xs">
                  {insights.top_api_errors.slice(0, 8).map((e, i) => (
                    <div key={`${e.server_id}-${i}`} className="border-b border-border/30 pb-2 last:border-0">
                      <div className="flex justify-between gap-2">
                        <span className="font-medium text-foreground truncate">{e.api_title}</span>
                        <span className="text-red-500 tabular-nums shrink-0">{e.failures}</span>
                      </div>
                      {e.sample_error && (
                        <p className="text-muted-foreground truncate mt-0.5">{e.sample_error}</p>
                      )}
                    </div>
                  ))}
                  {insights.top_api_errors.length === 0 && (
                    <p className="text-muted-foreground text-center py-4">No failures recorded</p>
                  )}
                </div>
              </div>
              <div className="glass-strong rounded-2xl p-6 shadow-premium">
                <h3 className="text-sm font-bold text-foreground mb-4">Upgrade funnel</h3>
                <div className="space-y-4">
                  <FunnelStep
                    label="Signed in / up"
                    value={insights.upgrade_funnel.signed_up}
                    max={insights.upgrade_funnel.signed_up || 1}
                    color="bg-violet-500"
                  />
                  <FunnelStep
                    label="Created MCP"
                    value={insights.upgrade_funnel.mcp_created}
                    max={insights.upgrade_funnel.signed_up || 1}
                    color="bg-emerald-500"
                  />
                  <FunnelStep
                    label="Upgraded to Pro"
                    value={insights.upgrade_funnel.user_upgraded}
                    max={insights.upgrade_funnel.mcp_created || 1}
                    color="bg-primary"
                  />
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* ── Top events + User table ── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Top events */}
          <motion.div
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.16 }}
            className="glass-strong rounded-2xl p-6 shadow-premium"
          >
            <div className="flex items-center gap-2 mb-5">
              <Activity className="w-4 h-4 text-primary" />
              <h3 className="text-sm font-bold text-foreground">Top Events</h3>
            </div>
            <div className="space-y-2">
              {topEvents.slice(0, 12).map((e, i) => (
                <div key={e.event} className="flex items-center gap-3 text-xs">
                  <span className="w-5 text-muted-foreground tabular-nums text-right">{i + 1}</span>
                  <span className="flex-1 font-mono text-foreground truncate">{e.event}</span>
                  <span className="tabular-nums font-semibold text-foreground">{e.count.toLocaleString()}</span>
                  <span className="tabular-nums text-muted-foreground w-16 text-right">
                    {e.unique_visitors} uniq
                  </span>
                </div>
              ))}
              {topEvents.length === 0 && (
                <p className="text-xs text-muted-foreground text-center py-4">No events recorded yet</p>
              )}
            </div>
          </motion.div>

          {/* Users with emails */}
          <motion.div
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.18 }}
            className="glass-strong rounded-2xl p-6 shadow-premium"
          >
            <div className="flex items-center gap-2 mb-5">
              <Mail className="w-4 h-4 text-primary" />
              <h3 className="text-sm font-bold text-foreground">User Emails</h3>
              <span className="text-xs text-muted-foreground ml-auto">{users.length} users</span>
            </div>
            <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
              {users.map((u) => (
                <div key={u.email} className="flex items-start gap-3 py-1.5 border-b border-border/40 last:border-0">
                  <div className="w-7 h-7 rounded-full bg-primary/15 flex items-center justify-center text-primary text-xs font-bold flex-shrink-0">
                    {u.email.slice(0, 1).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-semibold text-foreground truncate">{u.email}</div>
                    <div className="text-[11px] text-muted-foreground">
                      First seen {fmtDate(u.first_seen_at)} · Last {fmtDate(u.last_seen_at)}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-0.5 flex-shrink-0">
                    {u.converted === 1 && (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800 text-[10px] font-semibold text-emerald-600 dark:text-emerald-400">
                        <Zap className="w-2.5 h-2.5" /> Converted
                      </span>
                    )}
                    {u.conversion_count > 0 && (
                      <span className="text-[10px] text-muted-foreground">{u.conversion_count}× MCP</span>
                    )}
                  </div>
                </div>
              ))}
              {users.length === 0 && (
                <p className="text-xs text-muted-foreground text-center py-4">No signed-in users yet</p>
              )}
            </div>
          </motion.div>
        </div>

        {/* ── Live event feed ── */}
        <motion.div
          initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.2 }}
          className="glass-strong rounded-2xl p-6 shadow-premium"
        >
          <div className="flex items-center gap-2 mb-5">
            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse flex-shrink-0" />
            <h3 className="text-sm font-bold text-foreground">Live Event Feed</h3>
            <span className="text-xs text-muted-foreground ml-auto">Most recent 40 events</span>
          </div>
          <div className="space-y-1 max-h-80 overflow-y-auto">
            {recent.map((e, i) => (
              <div
                key={i}
                className="grid grid-cols-[1fr_auto_auto] gap-3 items-center py-1.5 border-b border-border/30 last:border-0 text-xs"
              >
                <div className="flex items-center gap-2 min-w-0">
                  {e.event === "error" ? (
                    <AlertTriangle className="w-3 h-3 text-red-500 flex-shrink-0" />
                  ) : e.event === "mcp_converted" ? (
                    <Zap className="w-3 h-3 text-primary flex-shrink-0" />
                  ) : (
                    <Activity className="w-3 h-3 text-muted-foreground flex-shrink-0" />
                  )}
                  <span className={`font-mono font-semibold truncate ${e.event === "error" ? "text-red-500" : e.event === "mcp_converted" ? "text-primary" : "text-foreground"}`}>
                    {e.event}
                  </span>
                  {e.email && (
                    <span className="text-muted-foreground truncate hidden sm:block">{e.email}</span>
                  )}
                </div>
                <span className="text-muted-foreground truncate max-w-[120px] hidden md:block">
                  {e.page ?? "—"}
                </span>
                <span className="text-muted-foreground tabular-nums whitespace-nowrap">
                  {new Date(e.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                </span>
              </div>
            ))}
            {recent.length === 0 && (
              <p className="text-xs text-muted-foreground text-center py-4">No events recorded yet — start using the app!</p>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default AdminPage;
